/**
 * Shared demo fixtures for the harness group. Pure data plus a small
 * streaming simulator used by the demos to exercise the delta fast path.
 * Tool-call and MCP fixtures grow with the tool-card components.
 */
import type { IHarnessMessage, THarnessStreamDelta } from './interfaces.js';

export const harnessDemoMarkdown = [
  '## Deploy summary',
  '',
  'The pipeline finished in **4m 12s** — one job regressed:',
  '',
  '| job | duration | baseline |',
  '| --- | --- | --- |',
  '| build | 61s | 58s |',
  '| tests | 122s | 119s |',
  '| sitemap | 41s | 13s |',
  '',
  '```typescript',
  "const cacheKey = hash(routeManifest); // pin to manifest, not locale",
  '```',
  '',
  'Recommendation: pin the sitemap cache key to the route manifest hash.',
].join('\n');

export const harnessDemoConversation = (): IHarnessMessage[] => [
  {
    id: 'demo-u1',
    role: 'user',
    text: 'Summarize the last deploy and flag anything that regressed.',
    createdAt: Date.now() - 420_000,
    attachments: [
      { id: 'demo-att1', name: 'deploy.log', mediaType: 'text/plain', size: 18_432, kind: 'text', truncated: true },
    ],
  },
  {
    id: 'demo-a1',
    role: 'assistant',
    text: harnessDemoMarkdown,
    createdAt: Date.now() - 400_000,
    reasoning: [
      {
        id: 'demo-r1',
        text: 'Compare job timings against the previous three runs; anything above 2× baseline is a regression worth naming.',
        startedAt: Date.now() - 415_000,
        endedAt: Date.now() - 411_000,
      },
    ],
    usage: { inputTokens: 1840, outputTokens: 310, totalTokens: 2150 },
  },
  {
    id: 'demo-u2',
    role: 'user',
    text: 'Prepare the cache-key patch please.',
    createdAt: Date.now() - 300_000,
  },
];

export interface IHarnessStreamSimulatorTarget {
  applyDelta(delta: THarnessStreamDelta): void;
}

/**
 * Streams a canned reply into a target (message list or chat) through the
 * delta fast path. Returns a stop function. The caller adds the (initially
 * empty, streaming) message to the target's `.messages` first.
 */
export const makeStreamingSimulator = (
  target: IHarnessStreamSimulatorTarget,
  messageId: string,
  options: { intervalMs?: number; chunkSize?: number; text?: string; withReasoning?: boolean } = {},
): (() => void) => {
  const text = options.text
    ?? 'Working on the patch: pinning the sitemap cache key to the route manifest hash. The change is three lines in `sitemap.job.ts`, plus a regression test that asserts the cache key stays stable across locale rollouts.';
  const intervalMs = options.intervalMs ?? 40;
  const chunkSize = options.chunkSize ?? 6;
  let offset = 0;
  let reasoningDone = !(options.withReasoning ?? true);
  const reasoningText = 'The cache key currently includes the locale list, which changed with the rollout — that is why every route re-crawled.';
  let reasoningOffset = 0;

  if (!reasoningDone) {
    target.applyDelta({ type: 'reasoning-start', messageId, partId: `${messageId}-r1`, startedAt: Date.now() });
  }

  const interval = setInterval(() => {
    if (!reasoningDone) {
      const chunk = reasoningText.slice(reasoningOffset, reasoningOffset + chunkSize * 2);
      reasoningOffset += chunkSize * 2;
      if (chunk) {
        target.applyDelta({ type: 'reasoning', messageId, partId: `${messageId}-r1`, delta: chunk });
        return;
      }
      target.applyDelta({ type: 'reasoning-end', messageId, partId: `${messageId}-r1`, endedAt: Date.now() });
      reasoningDone = true;
      return;
    }
    const chunk = text.slice(offset, offset + chunkSize);
    offset += chunkSize;
    if (!chunk) {
      clearInterval(interval);
      target.applyDelta({
        type: 'message-end',
        messageId,
        usage: { inputTokens: 980, outputTokens: 140, totalTokens: 1120 },
      });
      return;
    }
    target.applyDelta({ type: 'text', messageId, delta: chunk });
  }, intervalMs);

  return () => clearInterval(interval);
};
